// IE Hub - Application Entry Point
// Main entry point with React Router setup

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createHashRouter, RouterProvider } from "react-router-dom";

import AppShell from "./app/AppShell";
import { routes } from "./app/routes/index";
import ErrorBoundary from "./app/routes/ErrorBoundary";
import { initClarity } from "./shared/logging/clarity";
import { initLogRocket } from "./shared/logging/logrocket";
//import { initClarity } from './shared/logging/clarity';
import registerChunkReloadGuard from "./shared/lib/registerChunkReload";

// Import global styles
import "./index.css";
// Bundle local Roboto font
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";

// Legacy redirect: old "#/signup" should land on "#/auth" (prevents 404 in HashRouter)
const hash = window.location.hash;
if (
  hash === "#/signup" ||
  hash.startsWith("#/signup?") ||
  hash.startsWith("#/signup/")
) {
  window.location.replace(`/#/auth${hash.slice("#/signup".length)}`); // preserves ?query if present
}

// Service Worker registration for Firebase messaging
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.register("/firebase-messaging-sw.js");
}

// Normalize URL for HashRouter: if someone hits /something#/path, redirect to /#/path
// This keeps a single canonical shape for all routes in dev and prod
if (window.location.pathname !== "/" && window.location.hash) {
  const normalizedUrl = `/${window.location.hash}`; // preserves the hash including any query after it
  window.location.replace(normalizedUrl);
}

// Initialize LogRocket (production only, mirrors legacy behavior)
void initLogRocket();

// Initialize Microsoft Clarity (opt-in via env + project id)
void initClarity();

// Recover from stale chunk URLs by reloading once per session
registerChunkReloadGuard();

// Create router configuration (HashRouter for environment-agnostic deep links)
const router = createHashRouter(
  [
    {
      path: "/",
      element: <AppShell />,
      errorElement: (
        <ErrorBoundary>
          <div />
        </ErrorBoundary>
      ),
      children: routes,
    },
  ],
  {
    future: {
      // v7_startTransition: true // Removed due to TypeScript compatibility issues
    },
  }
);

// Get root element with proper type checking
const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

// Create root and render app
const root = createRoot(rootElement);

root.render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>
);
